/**
 * @file Runtime configuration schema (1.13.6, F2.4).
 *
 * The shape of /config.json, written by the container at start from
 * environment variables. Nothing here is secret: publishable keys and public
 * identifiers only. One build serves every environment, so which product this
 * page sells is configuration rather than a constant in the bundle.
 */
import { z } from 'zod';

/** Zod schema for /config.json. */
export const runtimeConfigSchema = z.object({
  /**
   * Where the API lives. In a deployment this is the API's own https origin,
   * because the browser calls it directly and the nginx image proxies no data
   * path; that origin must also be in the image's CSP connect-src. Locally it
   * is the path "/api", which the dev server proxies.
   */
  apiBaseUrl: z.string().min(1),
  /**
   * Slug of the membership product whose plans this page sells.
   *
   * A value rather than a constant because swapping which offer is live should
   * be a blueprint sync, not a release: the page reads `GET
   * /v1/checkout/plans/{slug}` and renders whatever the API says is
   * purchasable, including the price and the cadence.
   */
  membershipProductSlug: z.string().min(1).default('pretty-paid-society'),
  /**
   * Cloudflare Turnstile site key for the email form (F5.3, 1.13.6).
   *
   * Empty means no widget: no script is loaded and the magic-link request
   * carries no token, exactly as before the widget existed. Set, the form
   * renders the widget and sends its token as `turnstileToken`
   * (`lib/turnstile/useTurnstile.ts`). The API only enforces the token when
   * it holds the matching secret itself, so the two are set together per
   * environment.
   */
  turnstileSiteKey: z.string().default(''),
  /**
   * Stripe publishable key, for the card-change form on /membership (1.13.2).
   *
   * The only Stripe.js on this origin: checkout itself stays hosted, a
   * navigation to Stripe's page. Empty means no card change is offered and
   * Stripe.js is never loaded; cancelling and everything else still work.
   */
  stripePublishableKey: z.string().default(''),
  /** When true the whole page shows the maintenance notice instead. */
  maintenanceMode: z.boolean().default(false),
  /**
   * Sends client-side error reports to our own API (F1.9).
   *
   * Read by `lib/observability/reporter.ts`, a copy of the storefront's. Off,
   * a report is written to the console only; on, it is also posted to
   * `POST /v1/client-errors` on `apiBaseUrl`. There is deliberately no URL
   * here: the destination is a fixed path on `apiBaseUrl`, so this can never
   * be pointed at a third party, and the CSP's connect-src already names that
   * origin.
   */
  errorReportingEnabled: z.boolean().default(false),
});

/** Validated runtime configuration. */
export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>;
